import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, PillBottle, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Select } from '@/components/common/Select'
import { drugApi } from '@/api/drugApi'
import { drugCreateSchema } from '@/lib/validators'
import { DRUG_CATEGORY_OPTIONS } from '@/lib/constants'
import { useDrugStore } from '@/store/drugStore'

const TODAY = new Date().toISOString().slice(0, 10)

const blank = {
  name: '',
  category: '',
  dosage: '',
  stock_count: '',
  reorder_level: '',
  mfg_date: '',
  exp_date: '',
  mrp: '',
}

export default function AddDrug() {
  const navigate = useNavigate()
  const upsert = useDrugStore((s) => s.upsert)
  const [data, setData] = useState(blank)
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const set = (patch) => setData((d) => ({ ...d, ...patch }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})
    const parsed = drugCreateSchema.safeParse(data)
    if (!parsed.success) {
      const errs = {}
      parsed.error.issues.forEach((iss) => {
        errs[iss.path[0]] = iss.message
      })
      setErrors(errs)
      return
    }
    setSubmitting(true)
    try {
      // build a clean payload — strip optionals that are empty
      const payload = { ...parsed.data }
      if (!payload.dosage) delete payload.dosage
      if (!payload.mfg_date) delete payload.mfg_date
      if (payload.reorder_level === undefined) delete payload.reorder_level
      const created = await drugApi.create(payload)
      upsert(created)
      toast.success(`${created.name} added to stock`)
      navigate(`/drugs/${created.drug_id}`)
    } catch (err) {
      // axios interceptor already toasts; nothing else to do
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="container-page py-6 sm:py-8">
      <PageHeader
        eyebrow="Phase 6 — Inventory"
        title="Add a medicine"
        description="Register a new drug in the inventory. Initial stock is logged as an 'in' movement in the audit trail."
        actions={
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back
          </Button>
        }
      />

      <form
        onSubmit={handleSubmit}
        className="mx-auto mt-6 max-w-3xl space-y-5 rounded-2xl bg-white p-5 shadow-soft ring-1 ring-ink-100 sm:p-7"
      >
        <div className="flex items-center gap-3 pb-2">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
            <PillBottle className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold text-ink-900">Medicine details</p>
            <p className="text-xs text-ink-500">
              ID like <span className="font-mono">RD000DR…</span> auto-generates on save.
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Medicine name"
            required
            name="name"
            value={data.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="e.g. Amoxicillin 500mg"
            error={errors.name}
            className="sm:col-span-2"
          />
          <Select
            label="Category"
            required
            name="category"
            value={data.category}
            onChange={(e) => set({ category: e.target.value })}
            placeholder="Select category"
            options={DRUG_CATEGORY_OPTIONS}
            error={errors.category}
          />
          <Input
            label="Dosage / strength"
            name="dosage"
            value={data.dosage}
            onChange={(e) => set({ dosage: e.target.value })}
            placeholder="e.g. 500 mg, 10 ml"
            error={errors.dosage}
          />
          <Input
            label="Initial stock"
            required
            type="number"
            min="0"
            name="stock_count"
            value={data.stock_count}
            onChange={(e) => set({ stock_count: e.target.value })}
            placeholder="0"
            error={errors.stock_count}
          />
          <Input
            label="Reorder level"
            type="number"
            min="0"
            name="reorder_level"
            value={data.reorder_level}
            onChange={(e) => set({ reorder_level: e.target.value })}
            placeholder="Leave empty for default"
            hint="Falls back to clinic-wide low-stock default."
            error={errors.reorder_level}
          />
          <Input
            label="Manufacture date"
            type="date"
            name="mfg_date"
            value={data.mfg_date}
            onChange={(e) => set({ mfg_date: e.target.value })}
            max={TODAY}
            error={errors.mfg_date}
          />
          <Input
            label="Expiry date"
            required
            type="date"
            name="exp_date"
            value={data.exp_date}
            onChange={(e) => set({ exp_date: e.target.value })}
            error={errors.exp_date}
          />
          <Input
            label="MRP (₹) per unit"
            required
            type="number"
            min="0"
            step="0.01"
            name="mrp"
            value={data.mrp}
            onChange={(e) => set({ mrp: e.target.value })}
            placeholder="0.00"
            error={errors.mrp}
            hint="Per tablet / per tube / per bottle — the unit on a bill."
            className="sm:col-span-2"
          />
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-ink-100 pt-4 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={submitting}
            leftIcon={!submitting ? <Save className="h-4 w-4" /> : null}
          >
            Save medicine
          </Button>
        </div>
      </form>
    </div>
  )
}
